import { Spin, Table } from 'antd';
import { useMemo, useState } from 'react';
import type { Key } from 'react';
import styled from '@emotion/styled';

import { colorConstants } from 'config/colorConstants';
import type { LensCatalogData } from '../ScenarioLensSection/scenarioLensCatalog.types';
import { buildLensTableColumns } from './buildLensTableColumns';
import { buildLensPlaceholderRows } from './buildLensPlaceholderRows';

const StyledTableShell = styled.div`
    min-height: 120px;
    background: ${colorConstants.white};
    border-radius: 4px;
    overflow: hidden;

    .ant-table-thead > tr > th {
        font-size: 13px;
        font-weight: 600;
        background: ${colorConstants.greyAlt10};
        color:${colorConstants.blackTitle};;
    }

    .ant-table-tbody > tr > td {
        background: ${colorConstants.white};
    }

    .ant-table-cell {
        font-size: 13px;
    }

    .ant-table-thead .ant-table-cell-fix-left,
    .ant-table-thead .ant-table-cell-fix-left-first,
    .ant-table-thead .ant-table-cell-fix-left-last,
    .ant-table-thead .ant-table-selection-column {
        background: ${colorConstants.greyAlt19} !important;
        z-index: 3 !important;
    }

    .ant-table-tbody .ant-table-cell-fix-left,
    .ant-table-tbody .ant-table-cell-fix-left-first,
    .ant-table-tbody .ant-table-cell-fix-left-last,
    .ant-table-tbody .ant-table-selection-column {
        background: ${colorConstants.white} !important;
        z-index: 2 !important;
    }

    .ant-table-tbody > tr.ant-table-row:hover > .ant-table-cell-fix-left,
    .ant-table-tbody > tr.ant-table-row:hover > .ant-table-cell-fix-left-first,
    .ant-table-tbody > tr.ant-table-row:hover > .ant-table-cell-fix-left-last,
    .ant-table-tbody > tr.ant-table-row:hover > .ant-table-selection-column {
        background: ${colorConstants.white} !important;
    }

    .ant-table-selection-column {
        width: 48px !important;
        min-width: 48px !important;
        padding-right: 4px !important;
    }

    .ant-table-cell::before {
        display: none !important;
    }

    .lens-table-ai-column {
        padding-top: 8px !important;
        padding-bottom: 8px !important;
    }

`;

const StyledLoadingOverlay = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 120px;
`;

const StyledEmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 120px;
    padding: 24px 16px;
    text-align: center;
    font-size: 14px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.45);
`;

type ScenarioLensTableShellProps = {
    catalog: LensCatalogData | null;
    isLoading?: boolean;
    lensName?: string | null;
};

const ScenarioLensTableShell = ({ catalog, isLoading = false, lensName }: ScenarioLensTableShellProps) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const { columns, hasSelection, hasRowSelection } = useMemo(
        () =>
            catalog
                ? buildLensTableColumns(catalog)
                : { columns: [], hasSelection: false, hasRowSelection: false },
        [catalog],
    );

    const placeholderRows = useMemo(
        () => (catalog && hasSelection ? buildLensPlaceholderRows(catalog) : []),
        [catalog, hasSelection],
    );

    if (isLoading && !catalog) {
        return (
            <StyledLoadingOverlay>
                <Spin size="small" />
            </StyledLoadingOverlay>
        );
    }

    if (!catalog || !hasSelection) {
        return (
            <StyledEmptyState>
                {lensName
                    ? `Select attributes in lens settings for ${lensName} to display table columns.`
                    : 'Select attributes in lens settings to display table columns.'}
            </StyledEmptyState>
        );
    }

    return (
        <StyledTableShell>
            <Table
                columns={columns}
                dataSource={placeholderRows}
                rowKey="key"
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="small"
                bordered
                rowSelection={
                    hasRowSelection
                        ? {
                              selectedRowKeys,
                              onChange: setSelectedRowKeys,
                              columnWidth: 48,
                          }
                        : undefined
                }
            />
        </StyledTableShell>
    );
};

export default ScenarioLensTableShell;
